import { useNavigate } from '@tanstack/react-router';
import type { Editor } from '@tiptap/react';
import { useCombobox } from './useCombobox';

export const useComboboxKeyHandler = (editor: Editor | null) => {
  const navigate = useNavigate();
  const { state } = useCombobox();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { collection, selectionManager } = state;
    const { focusedKey } = selectionManager;

    // Enter and Escape must be handled first, before the isOpen check below.
    // These should always work regardless of popover state.
    if (e.key === 'Enter') {
      e.preventDefault();

      if (focusedKey) {
        selectionManager.setSelectedKeys([focusedKey]);
      }

      state.close();
      editor?.commands.blur();
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();

      state.close();
      editor?.commands.blur();
      return;
    }

    if (!state.isOpen) {
      if (e.key.length === 1) {
        state.open();
      }

      return;
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && editor?.isEmpty) {
      navigate({ to: '/search', search: {} });
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      const next = focusedKey
        ? collection.getKeyAfter(focusedKey)
        : collection.getFirstKey();

      if (next) {
        selectionManager.setFocusedKey(next);
      }
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();

      const prev = focusedKey
        ? collection.getKeyBefore(focusedKey)
        : collection.getLastKey();

      if (prev) {
        selectionManager.setFocusedKey(prev);
      }
    }
  };

  return { handleKeyDown };
};
